import React, { useState } from 'react';
import styled from 'styled-components/macro';

import {
  GQLOperations,
  Milestone,
  Rule,
  useUpdateMilestoneReportTemplateMutation,
} from 'types/dt-graphql-apollo-v2';
import { Title, Body, Button, Modal, Flex } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import { getSchemaTypeForRule } from 'utils/data-validation';
import { onlyUnique } from 'utils/misc';
import { HorizontalDivider } from 'components';
import { useAccessControl } from 'hooks/useAccessControl';
import { Project, useGetProjectQuery } from 'hooks/projects';
import { SchemaAttributesSelection } from './SchemaAttributesSelection';

type ReportData = Record<string, string[]>;

export type RunAttributesSelectionProps = {
  disabled?: boolean;
  loading?: boolean;
  milestone?: Milestone;
  onCancel: VoidFunction;
  onCreateReport: (reportData: ReportData) => void;
  rules: Rule[];
  schemaCollectionRefId?: string | null;
  visible: boolean;
};

const initializeReportData = (reportTemplate?: string | null) => {
  try {
    const reportData = JSON.parse(reportTemplate ?? '');

    if (
      !(
        typeof reportData === 'object' &&
        Object.values(reportData).every((value) => Array.isArray(value))
      )
    ) {
      throw Error('Report template has incorrect structure');
    }

    return reportData;
  } catch (error) {
    return {};
  }
};

export const RunAttributesSelection = ({
  disabled = false,
  loading = false,
  milestone,
  onCancel,
  onCreateReport,
  rules,
  schemaCollectionRefId,
  visible,
}: RunAttributesSelectionProps) => {
  const [selectedSchemaId, setSelectedSchemaId] = useState('');
  const [reportData, setReportData] = useState<ReportData>(
    initializeReportData(milestone?.reportTemplate)
  );
  const hasAccess = useAccessControl();
  const canEditCollection = hasAccess(['Update_Milestone']);

  // Get the schema information from ITG
  const { data: dataProject } = useGetProjectQuery(schemaCollectionRefId);
  const project = dataProject?.Project?.[0] as Project;
  const [updateReportTemplate, updateReportTemplateResult] =
    useUpdateMilestoneReportTemplateMutation();

  const schemaTypes = rules
    ?.map((rule) => getSchemaTypeForRule(rule))
    .filter(onlyUnique);

  /** Reset the report state and also delete the report draft saved in the localStorage. */
  const resetReport = () => {
    setReportData({});
  };

  /** Add the attributes for a selected schema type into the reportData and
   * saves the new report as a draft in the localStorage.
   */
  const handleSchemaAttributesChange = (
    schemaType: string,
    attributes: string[]
  ) => {
    setReportData((currentState) => {
      const newState = {
        ...currentState,
        [schemaType]: attributes,
      };
      return newState;
    });
  };

  const saveReportTemplate = async () => {
    if (!milestone?.externalId) return;

    try {
      const reportTemplate = JSON.stringify(reportData);

      await updateReportTemplate({
        variables: {
          id: milestone.externalId,
          name: milestone.name,
          reportTemplate,
        },
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
    } catch (error) {
      /* ignore error here */
    }
  };

  const renderHeader = () => (
    <StyledHeader>
      <Title level={3}>Create report</Title>
      <Body level={6}>
        Create a report from the run and download as a file. The result is found
        in the “Data Quality Explorer”.
      </Body>
      <Body level={6}>
        Customize the report by adding the attributes that you want to include
        in the result file. The attributes are selected per schema type, which
        are added to the rules applied to these schema types.
      </Body>
    </StyledHeader>
  );

  const renderSchemaTypes = () => (
    <>
      <Title style={{ margin: '1rem 0' }} level={4}>
        Select attributes to include in the report file
      </Title>
      <StyledSchemaList>
        {schemaTypes.map((st) => st && renderSchemaTypeSelection(st))}
      </StyledSchemaList>
    </>
  );

  const renderSchemaTypeSelection = (schemaTypeName: string) => {
    const currentSchema = project?.schemas?.find(
      (schema) => schema?.name === schemaTypeName
    );

    const attributesList = reportData[currentSchema?.name || ''] || [];

    return (
      // Handle the attributes for a given schema type
      <SchemaAttributesSelection
        selectedSchemaId={selectedSchemaId}
        currentSchema={currentSchema}
        schemas={project?.schemas}
        attributesList={attributesList}
        setSelectedSchemaId={setSelectedSchemaId}
        onSchemaAttributesChange={handleSchemaAttributesChange}
      />
    );
  };

  const renderReportTemplateSaver = () => {
    if (!canEditCollection) return null;

    const {
      data,
      error,
      loading: isLoadingUpdate,
    } = updateReportTemplateResult;
    const templateHasChanges =
      milestone?.reportTemplate !== JSON.stringify(reportData);

    return (
      <StyledSaveReportContainer>
        <HorizontalDivider style={{ margin: '8px 0' }} />

        <Flex alignItems="center" gap={8}>
          <Button
            disabled={!templateHasChanges}
            loading={isLoadingUpdate}
            onClick={saveReportTemplate}
          >
            Save report settings
          </Button>

          {data && (
            <StyledSaveResult className="success">
              <Icon type="Checkmark" />
              <Body level={2}>Report settings successfully saved</Body>
            </StyledSaveResult>
          )}

          {error && (
            <StyledSaveResult className="error">
              <Icon type="Error" />
              <Body level={2}>Report settings could not be saved</Body>
            </StyledSaveResult>
          )}
        </Flex>

        <StyledSaveReportNotice>
          <Icon type="Info" />
          <Body level={2} style={{ color: 'var(--cogs-primary)' }}>
            Saved report settings affect the reporting of this collection for
            all users.
          </Body>
        </StyledSaveReportNotice>
      </StyledSaveReportContainer>
    );
  };

  const renderFooter = () => (
    <StyledRow>
      <Button
        size="small"
        icon="Delete"
        aria-label="Remove all attributes for every schema type"
        disabled={Object.keys(reportData).length === 0}
        onClick={resetReport}
      >
        Reset all attributes
      </Button>
      <div>
        <Button
          type="ghost"
          onClick={onCancel}
          style={{ marginRight: '1rem' }}
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={() => onCreateReport(reportData)}
          disabled={disabled}
          loading={loading}
        >
          Run and create report
        </Button>
      </div>
    </StyledRow>
  );

  return (
    <StyledModal hideFooter visible={visible} onCancel={onCancel}>
      {renderHeader()}
      {renderSchemaTypes()}
      {renderReportTemplateSaver()}
      {renderFooter()}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  overflow: auto;
  width: 760px !important;
  max-width: calc(90% - 2rem);
  .cogs-modal-content {
    padding: 0;
    min-height: 200px;
    position: relative;
    overflow-wrap: break-word;
    margin: 1rem 2rem;
  }
  .cogs-icon-Close {
    cursor: pointer;
  }
`;

const StyledHeader = styled.div`
  margin-bottom: 2rem;

  .cogs-title-3 {
    margin: 1rem 0;
  }
  .cogs-body-6 {
    margin: 1rem 0;
  }
`;

const StyledSchemaList = styled.div`
  min-height: 20vh;
  overflow: auto;
`;

const StyledRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const StyledSaveReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 40px;
`;

const StyledSaveResult = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;

  &.error {
    .cogs-icon,
    .cogs-body-2 {
      color: var(--cogs-text-warning);
    }
  }

  &.success {
    .cogs-icon,
    .cogs-body-2 {
      color: var(--cogs-graphics-success);
    }
  }
`;

const StyledSaveReportNotice = styled.div`
  align-items: center;
  color: var(--cogs-primary);
  display: flex;
  gap: 8px;
`;
